import { useCallback } from 'react';
import { useBookingContext } from '../lib/context/BookingContext/BookingContext';
import { BookingData } from '../lib/context/BookingContext/bookingTypes';

type InitializeNewAppointmentParams = Omit<BookingData, 'bookingType' | 'previousApptId'> &
  Required<Pick<BookingData, 'caseId'>>;

type InitializeNewCaseBookingParams = Omit<BookingData, 'caseId' | 'bookingType' | 'previousApptId'> &
  Required<Pick<BookingData, 'newCaseDetails'>>;

type InitializeRescheduleParams = Omit<BookingData, 'bookingType' | 'previousApptId'> &
  Required<Pick<BookingData, 'caseId' | 'previousApptId'>>;

type InitializeF2FParams = Omit<BookingData, 'bookingType' | 'previousApptId'> &
  Required<Pick<BookingData, 'caseId' | 'previousApptId'>>;

export const useBookingFlowControl = () => {
  const { setBookingContext, patchBookingContext } = useBookingContext();

  const initializeNewAppointmentBooking = useCallback((initParams: InitializeNewAppointmentParams) => {
    setBookingContext({
      bookingType: 'BOOK',
      ...initParams
    });
  }, []);

  const initializeNewCaseAppointmentBooking = useCallback((initParams: InitializeNewCaseBookingParams) => {
    setBookingContext({
      isNewCase: true,
      bookingType: 'BOOK',
      ...initParams
    });
  }, []);

  const initializeRescheduleBooking = useCallback(({ previousApptId, ...initParams }: InitializeRescheduleParams) => {
    if (!previousApptId) {
      throw new Error('previousApptId is required for reschedule');
    }

    setBookingContext({
      previousApptId,
      bookingType: 'RESCHEDULE',
      ...initParams
    });
  }, []);

  const initializeF2FBooking = useCallback((initParams: InitializeF2FParams) => {
    setBookingContext({
      bookingType: 'F2F-BOOK',
      ...initParams
    });
  }, []);

  const initializeCaseReviewSchedule = useCallback((initParams: InitializeNewAppointmentParams) => {
    setBookingContext({
      bookingType: 'CASE-REVIEW',
      ...initParams
    });
  }, []);

  const initializeNewCaseCaseReviewSchedule = useCallback((initParams: InitializeNewCaseBookingParams) => {
    setBookingContext({
      isNewCase: true,
      bookingType: 'CASE-REVIEW',
      ...initParams
    });
  }, []);

  const updateAppointmentCallType = useCallback((callType: BookingData['callType']) => {
    patchBookingContext({ callType });
  }, []);

  const updateAppointmentClinicianType = useCallback((clinType: BookingData['clinType']) => {
    patchBookingContext({ clinType });
  }, []);

  const updateAppointmentClinicianSlot = useCallback(
    ({ apptDate, clinician }: Pick<BookingData, 'apptDate' | 'clinician'>) => {
      patchBookingContext({ apptDate, clinician });
    },
    []
  );

  const updateCaseReviewClinician = useCallback((clinician: BookingData['clinician']) => {
    patchBookingContext({ clinician });
  }, []);

  const resetAppointmentSlotSelection = useCallback(() => {
    patchBookingContext({ apptDate: null, clinician: null, callType: null });
  }, []);

  return {
    initializeNewAppointmentBooking,
    initializeNewCaseAppointmentBooking,
    initializeRescheduleBooking,
    initializeF2FBooking,
    initializeCaseReviewSchedule,
    initializeNewCaseCaseReviewSchedule,
    updateAppointmentClinicianType,
    updateAppointmentCallType,
    updateAppointmentClinicianSlot,
    resetAppointmentSlotSelection,
    updateCaseReviewClinician
  };
};

export default useBookingFlowControl;
